import { PopoverPlacement } from "@idui/react-popover";
import clsx from "clsx";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  useCrudNavigate,
  useDropdownItemsCtxProvider,
  usePassengerTypeTitle,
  useRideStationContext,
  useRtl,
} from "@ryde/redux";
import {
  canRemovePassengerByProfileScope,
  computeRideStationTypeSingle,
  DragTypeEnum,
  enablePassengerGenderDistinctionSelector,
  formatPhoneLocal,
  generateDragIdGridPassenger,
  getPassengerNameColor,
  getPassengerScopeRestrictions,
  hasPassengerScopeRestrictions,
  isHubRideCompanionPassenger,
  isRideGreyed,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  rideBookingsAF,
  rideSelectedPassengersIdsAF,
  RideStationPanelMode,
  showPassengerTypeInsteadOfPhoneOnStationsPanelSelector,
  useDrawers,
  useFormats,
  useHasPermissions,
  useIsMobile,
  useProfileAuthorizationCheck,
  type IRideStationPassengerRow,
} from "@ryde/shared";
import { blueMui } from "@ryde/theme";

import { CheckboxControlledMui } from "../../../../controls";
import { CellSimple } from "../../../../grid";
import { useDecorateWithMarkLocal } from "../../../../utils";
import { DraggableWrapper } from "../../../draggable";
import {
  HubRideCompanionMark,
  RideBoardingIndicator,
  RidePassengersInOutIcon,
} from "../../../ride-controls";
import { FramerTooltip } from "../../../tooltips";
import { RidePassengerTooltip } from "../../passenger-tooltip";
import { getShiftTimeColor } from "./ride-station-panel.utils";
import { PassengerGenderCell } from "./ride-station-passenger-gender-cell";
import {
  getPassengerContactCellColor,
  getPassengerShiftControlState,
  isGridPanelPassengerDraggable,
} from "./ride-station-passenger.row.utils";
import {
  PassengerRowFlightNumberCell,
  RideStationPassengerRowStyled,
} from "./ride-stations.styled";

export const RideStationPassengerRow: FC<IRideStationPassengerRow> = memo(
  props => {
    const { passenger, deletePassengerHandler, isDragClone } = props;
    const {
      mode = RideStationPanelMode.GRID_PANEL,
      station,
      rideDto,
    } = useRideStationContext();
    const { openEditFormDrawer } = useDrawers();
    const { getDropdownLabel } = useDropdownItemsCtxProvider();
    const { navigateToEdit } = useCrudNavigate("flight");
    const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
    const { hasPermissions: hasCreateOtherSitePermission } = useHasPermissions({
      entity: "ride",
      action: "create_other_site",
    });

    const showPassengerTypeInsteadOfPhone = useRecoilValue(
      showPassengerTypeInsteadOfPhoneOnStationsPanelSelector,
    );
    const isGenderDistinctionEnabled = useRecoilValue(
      enablePassengerGenderDistinctionSelector,
    );

    const { hasPermissions: isUpdatePassengerPermission } = useHasPermissions({
      entity: "passenger",
      action: "update",
    });

    const { hasPermissions: isUpdatePassengerTypePermission } =
      useHasPermissions({
        entity: "passengerType",
        action: "update",
      });
    const { hasPermissions: isUpdateRidePermission } = useHasPermissions({
      entity: "ride",
      action: "update",
    });

    const { hasPermissions: isEditBookingPermission } = useHasPermissions({
      entity: "booking",
      action: "update",
    });

    const hasScopeRestrictions = useMemo(
      () =>
        hasPassengerScopeRestrictions(
          getPassengerScopeRestrictions(passenger, profileAuthorizationInCheck),
        ),
      [passenger, profileAuthorizationInCheck],
    );
    const isPassengerInProfileAuthScope = !hasScopeRestrictions;
    const canRemovePassengerByScope = canRemovePassengerByProfileScope(
      passenger,
      profileAuthorizationInCheck,
      { hasCreateOtherSite: hasCreateOtherSitePermission },
    );

    const { id: stationId = 0, isLocked = false } = station || {};
    const { id: rideId = 0 } = rideDto || {};
    const {
      firstName,
      lastName,
      id: passengerId,
      flightId,
      bookingId,
      passengerTypeId,
      isJoined,
      shiftHour,
      shiftHourByArrival,
    } = passenger;

    const passengerTypeTitle = usePassengerTypeTitle(passengerTypeId);

    const [passengerFlightNumber, setPassengerFlightNumber] = useState<
      string | null
    >(null);

    const [selectedPassIds, setSelectedPassIds] = useRecoilState(
      rideSelectedPassengersIdsAF(rideDto?.id || 0),
    );
    const rideBookings = useRecoilValue(rideBookingsAF(rideDto?.id || 0));

    const { isMobile } = useIsMobile();
    const isGreyed = isRideGreyed(rideDto);
    const chosen = useMemo(() => {
      if (!selectedPassIds?.length) {
        return false;
      }
      const selectedPassIdsSet = new Set(selectedPassIds);
      return selectedPassIdsSet.has(passengerId);
    }, [passengerId, selectedPassIds]);

    const { formatTimeNormalized } = useFormats();

    const togglePassengerSelected = useCallback(
      (passId: number) => () => {
        setSelectedPassIds(selectedIds => {
          const selectedIdsSet = new Set(selectedIds);
          if (!selectedIdsSet.has(passId)) {
            return [...selectedIds, passId];
          } else {
            return selectedIds.filter(x => x !== passId);
          }
        });
      },
      [setSelectedPassIds],
    );

    const handlePassengerNameClick = useEvent(() => {
      if (
        isDragClone ||
        !isUpdatePassengerPermission ||
        !isPassengerInProfileAuthScope
      ) {
        return;
      }

      openEditFormDrawer({
        entity: "passenger",
        id: passengerId,
      });
    });

    const handlePassengerTypeClick = useEvent(() => {
      if (isDragClone || !isUpdatePassengerTypePermission || !passengerTypeId) {
        return;
      }

      openEditFormDrawer({
        entity: "passengerType",
        id: passengerTypeId,
      });
    });

    const fallbackBooking = useMemo(() => {
      return rideBookings?.find(b => b.passengerId === passengerId);
    }, [rideBookings, passengerId]);

    const bookingIdToUse = bookingId || fallbackBooking?.id;

    const handleShiftTimeClick = useEvent(() => {
      if (isDragClone || !isEditBookingPermission || !bookingIdToUse) {
        return;
      }

      openEditFormDrawer({
        entity: "booking",
        id: bookingIdToUse,
      });
    });

    const statusType = useMemo(
      () => computeRideStationTypeSingle(passenger, stationId),
      [passenger, stationId],
    );

    const { decorateValue } = useDecorateWithMarkLocal();
    const { t } = useTranslation();

    const isHubCompanion = isHubRideCompanionPassenger({
      passenger,
      rideDto,
    });
    const companionLabel = isHubCompanion
      ? t("ride.HUB_RIDE_COMPANION")
      : undefined;

    const passengerFullName = `${firstName || ""} ${lastName || ""}`;
    const currentStationId = stationId || undefined;

    const passengerDecoratedWithMarkName = useMemo(() => {
      return decorateValue(passengerFullName);
    }, [decorateValue, passengerFullName]);

    const isPassengerDraggable = isGridPanelPassengerDraggable({
      isLocked,
      mode,
      isUpdateRidePermission,
    });

    const passengerNameColor = getPassengerNameColor({
      isGreyed,
      isScopeRestricted: hasScopeRestrictions,
    });

    const displayShiftTime = shiftHour || shiftHourByArrival;
    const {
      isClickable: isShiftControlClickable,
      isReadOnly: isShiftControlReadOnly,
    } = getPassengerShiftControlState({
      isEditBookingPermission,
      bookingIdToUse,
      displayShiftTime,
    });

    const shiftTimeColor = getShiftTimeColor({
      bookingId,
      shiftHour,
      shiftHourByArrival,
      isJoined,
    });

    const { isRtl } = useRtl();

    useEffect(() => {
      const loadPassengerFlightNumber = async (): Promise<void> => {
        if (!flightId) {
          setPassengerFlightNumber(null);
        } else {
          const flightLabel = await getDropdownLabel("flight", flightId);
          setPassengerFlightNumber(flightLabel);
        }
      };

      loadPassengerFlightNumber();
    }, [flightId, getDropdownLabel]);

    const renderContent = (): JSX.Element => {
      const seatsCount = passenger.seats || 1;
      const seatsLabel =
        seatsCount === 1
          ? t("passenger.seats.single")
          : t("passenger.seats.plural");
      const seatsTooltip = `${seatsCount} ${seatsLabel}`;

      return (
        <RideStationPassengerRowStyled
          data-e2e-id={passengerId}
          data-passenger-id={passengerId}
          $isDragClone={isDragClone}
        >
          <div className="column_grip">
            {isPassengerDraggable && (
              <SVG_MUI.GripVertical3SVG className="pointer" />
            )}
          </div>
          <div className="column_checkbox">
            {!isGreyed && (
              <CheckboxControlledMui
                checked={!!chosen}
                setCheckedToggle={togglePassengerSelected?.(passengerId)}
                width={15}
                height={15}
              />
            )}
          </div>
          <div className="column_station_type">
            <RidePassengersInOutIcon
              statusType={statusType}
              isGreyed={isGreyed}
            />
          </div>
          <div className="column_family">
            {isHubCompanion && (
              <HubRideCompanionMark className="hub-ride-companion-mark" />
            )}
            <div className="passenger-name-tooltip-wrap">
              <RidePassengerTooltip
                passenger={passenger}
                companionLabel={companionLabel}
                currentStationId={currentStationId}
                rideSiteId={rideDto?.siteId}
                rideSiteName={rideDto?.siteName}
                rtlStrippedBottomLeftPlacement
                tooltipDisabled={isDragClone}
              >
                <span
                  className={clsx(
                    "truncate-text passenger-name-truncate-inner",
                    {
                      pointer:
                        isUpdatePassengerPermission &&
                        isPassengerInProfileAuthScope,
                    },
                  )}
                  onClick={handlePassengerNameClick}
                  onKeyDown={onActivateKeyDown(handlePassengerNameClick)}
                  role="button"
                  tabIndex={0}
                  style={{ color: passengerNameColor }}
                >
                  {passengerDecoratedWithMarkName}
                </span>
              </RidePassengerTooltip>
            </div>
          </div>
          <div className="column_pax">
            <FramerTooltip
              content={seatsTooltip}
              placement={PopoverPlacement.top}
            >
              <span
                className={clsx({
                  hover_show: !isMobile && (passenger.seats || 1) <= 1,
                  chosen: chosen,
                })}
              >
                {passenger.seats}
              </span>
            </FramerTooltip>
          </div>
          <div className="column_unit truncate-text">
            <CellSimple tooltipDisabled={!passenger.unitName}>
              {passenger.unitName || NO_VALUE_DISPLAY}
            </CellSimple>
          </div>
          <PassengerGenderCell
            isGenderDistinctionEnabled={isGenderDistinctionEnabled}
            sex={passenger.sex}
          />
          <div className="column_shift">
            {isShiftControlReadOnly && (
              <CellSimple color={shiftTimeColor} tooltipDisabled={true}>
                {formatTimeNormalized(displayShiftTime)}
              </CellSimple>
            )}
            {isShiftControlClickable && (
              <CellSimple
                color={shiftTimeColor}
                tooltipDisabled={true}
                onClick={handleShiftTimeClick}
                classNamesAdded="pointer"
              >
                {formatTimeNormalized(displayShiftTime)}
              </CellSimple>
            )}
          </div>
          {!showPassengerTypeInsteadOfPhone && (
            <div className="column_phone">
              <a
                href={`tel:${passenger.phone}`}
                className={clsx({
                  hover_show: !isMobile,
                  chosen,
                })}
              >
                <CellSimple
                  color={getPassengerContactCellColor(isGreyed)}
                  tooltipDisabled={isDragClone}
                >
                  {formatPhoneLocal(passenger.phone)}
                </CellSimple>
              </a>
            </div>
          )}
          {!!showPassengerTypeInsteadOfPhone && (
            <div
              className={clsx("truncate-text", {
                pointer: isUpdatePassengerTypePermission && passengerTypeId,
              })}
            >
              <CellSimple
                color={getPassengerContactCellColor(isGreyed)}
                tooltipDisabled={!passenger.passengerTypeId || isDragClone}
                onClick={handlePassengerTypeClick}
              >
                {passengerTypeTitle || NO_VALUE_DISPLAY}
              </CellSimple>
            </div>
          )}

          {passengerFlightNumber && (
            <div className="column_flight">
              <PassengerRowFlightNumberCell
                className={clsx({
                  pointer: true,
                })}
                onClick={() => flightId && navigateToEdit(flightId)}
              >
                <CellSimple color={blueMui.A400} tooltipDisabled={true}>
                  {passengerFlightNumber}
                </CellSimple>
              </PassengerRowFlightNumberCell>
            </div>
          )}

          {!isMobile && (
            <div
              className={clsx("column_bin", {
                // RYDE-7633: no passenger removal on a locked/greyed ride (e.g. past taxi-supplier).
                hidden:
                  !isUpdateRidePermission ||
                  !canRemovePassengerByScope ||
                  isGreyed,
              })}
            >
              <span
                className={clsx("bin", {
                  chosen: chosen && isUpdateRidePermission,
                })}
              >
                <FramerTooltip
                  content={t("ride.RemovePassenger")}
                  placement={
                    isRtl
                      ? PopoverPlacement.bottomRight
                      : PopoverPlacement.bottomLeft
                  }
                >
                  <SVG_MUI.Trash2SVG
                    className="pointer"
                    onClick={e => deletePassengerHandler?.(e, passenger.id)}
                  />
                </FramerTooltip>
              </span>
            </div>
          )}
          <RideBoardingIndicator passenger={passenger} />
        </RideStationPassengerRowStyled>
      );
    };

    return (
      <>
        {!isDragClone && isPassengerDraggable && (
          <DraggableWrapper
            id={generateDragIdGridPassenger(rideId, passenger.id)}
            dragData={{
              type: DragTypeEnum.GRID_PASSENGER,
              payload: {
                ...props,
                dragSelectedIds: selectedPassIds,
              },
            }}
          >
            {renderContent()}
          </DraggableWrapper>
        )}
        {(isDragClone || !isPassengerDraggable) && <> {renderContent()}</>}
      </>
    );
  },
);
